import type { Basic, Reference } from '@medplum/fhirtypes';
import {
  AUTH_AUTHORIZED_USES_EXT,
  AUTH_COVERAGE_REF_EXT,
  AUTH_LIMIT_TYPE_EXT,
  AUTH_NOTES_EXT,
  AUTH_NUMBER_EXT,
  AUTH_REMINDER_THRESHOLD_EXT,
  AUTH_REMIND_MONTH_BEFORE_EXPIRY_EXT,
  AUTH_MANUAL_INACTIVE_EXT,
  AUTH_SERVICE_TYPE_EXT,
  AUTH_USED_COUNT_EXT,
  AUTH_VALID_FROM_EXT,
  AUTH_VALID_THROUGH_EXT,
  AUTH_LIMIT_TYPE_CODES,
  INSURANCE_AUTHORIZATION_CODE,
  INSURANCE_AUTHORIZATION_CODE_SYSTEM,
  type AuthLimitTypeCode,
  type AuthStatusCode,
} from '@unify/fhir-model/constants';
import { toFhirDateOnlyLocal } from '@/lib/dateHelpers';

export interface InsuranceAuthorizationRecord {
  id: string;
  patientRef: string;
  coverageRef?: string;
  authorizationNumber: string;
  validFrom?: string;
  validThrough?: string;
  serviceType?: string;
  limitType: AuthLimitTypeCode;
  authorizedUses?: number;
  usedCount: number;
  remainingUses?: number;
  reminderThreshold?: number;
  remindMonthBeforeExpiring?: boolean;
  manualInactive?: boolean;
  status: AuthStatusCode;
  notes?: string;
}

function findExt(basic: Basic, url: string) {
  return basic.extension?.find((e) => e.url === url);
}

function extString(basic: Basic, url: string): string | undefined {
  return findExt(basic, url)?.valueString;
}

function extDate(basic: Basic, url: string): string | undefined {
  return findExt(basic, url)?.valueDate;
}

function extInt(basic: Basic, url: string): number | undefined {
  return findExt(basic, url)?.valueInteger;
}

function extRef(basic: Basic, url: string): string | undefined {
  return findExt(basic, url)?.valueReference?.reference;
}

export function isInsuranceAuthorizationBasic(resource: Basic): boolean {
  return resource.code?.coding?.some(
    (c) =>
      c.system === INSURANCE_AUTHORIZATION_CODE_SYSTEM &&
      c.code === INSURANCE_AUTHORIZATION_CODE,
  ) ?? false;
}

function extBool(basic: Basic, url: string): boolean | undefined {
  const ext = findExt(basic, url);
  if (ext?.valueBoolean != null) return ext.valueBoolean;
  return undefined;
}

export function computeAuthorizationStatus(
  input: Pick<
    InsuranceAuthorizationRecord,
    'limitType' | 'authorizedUses' | 'usedCount' | 'validFrom' | 'validThrough' | 'status' | 'manualInactive'
  >,
  now = new Date(),
): AuthStatusCode {
  if (input.manualInactive) return 'cancelled';
  if (input.status === 'cancelled' || input.status === 'pending') {
    return input.status;
  }
  const today = toFhirDateOnlyLocal(now);
  if (input.validThrough && input.validThrough < today) return 'expired';
  if (input.validFrom && input.validFrom > today) return 'pending';
  if (input.limitType === 'limited' && input.authorizedUses != null) {
    if (input.usedCount >= input.authorizedUses) return 'exhausted';
  }
  return 'active';
}

export function computeRemainingUses(
  limitType: AuthLimitTypeCode,
  authorizedUses?: number,
  usedCount = 0,
): number | undefined {
  if (limitType === 'unlimited') return undefined;
  if (limitType === 'date-range') return undefined;
  if (authorizedUses == null) return undefined;
  return Math.max(0, authorizedUses - usedCount);
}

export function authorizationWarning(
  record: InsuranceAuthorizationRecord,
  now = new Date(),
): { color: string; label: string } | null {
  const status = computeAuthorizationStatus(record, now);
  if (status === 'expired') return { color: 'red', label: 'Expired' };
  if (status === 'exhausted') return { color: 'red', label: 'Exhausted' };
  if (record.validThrough) {
    const daysLeft = Math.ceil(
      (new Date(record.validThrough).getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );
    const expiryReminderDays = record.remindMonthBeforeExpiring ? 30 : 14;
    if (daysLeft >= 0 && daysLeft <= expiryReminderDays) {
      return { color: 'orange', label: `Expires in ${daysLeft}d` };
    }
  }
  if (
    record.limitType === 'limited' &&
    record.remainingUses != null &&
    record.reminderThreshold != null &&
    record.remainingUses <= record.reminderThreshold
  ) {
    return { color: 'yellow', label: `${record.remainingUses} uses left` };
  }
  return null;
}

function extCode(basic: Basic, url: string): string | undefined {
  return findExt(basic, url)?.valueCode;
}

export function basicToAuthorization(basic: Basic): InsuranceAuthorizationRecord | null {
  if (!isInsuranceAuthorizationBasic(basic)) return null;
  const patientRef = basic.subject?.reference ?? '';
  const limitTypeRaw = extCode(basic, AUTH_LIMIT_TYPE_EXT) ?? extString(basic, AUTH_LIMIT_TYPE_EXT);
  const limitType = AUTH_LIMIT_TYPE_CODES.includes(limitTypeRaw as AuthLimitTypeCode)
    ? (limitTypeRaw as AuthLimitTypeCode)
    : 'unlimited';
  const authorizedUses = extInt(basic, AUTH_AUTHORIZED_USES_EXT);
  const usedCount = extInt(basic, AUTH_USED_COUNT_EXT) ?? 0;
  const remainingUses = computeRemainingUses(limitType, authorizedUses, usedCount);
  const draft: InsuranceAuthorizationRecord = {
    id: basic.id ?? '',
    patientRef,
    coverageRef: extRef(basic, AUTH_COVERAGE_REF_EXT),
    authorizationNumber: extString(basic, AUTH_NUMBER_EXT) ?? '',
    validFrom: extDate(basic, AUTH_VALID_FROM_EXT),
    validThrough: extDate(basic, AUTH_VALID_THROUGH_EXT),
    serviceType: extString(basic, AUTH_SERVICE_TYPE_EXT),
    limitType,
    authorizedUses,
    usedCount,
    remainingUses,
    reminderThreshold: extInt(basic, AUTH_REMINDER_THRESHOLD_EXT),
    remindMonthBeforeExpiring: extBool(basic, AUTH_REMIND_MONTH_BEFORE_EXPIRY_EXT) ?? false,
    manualInactive: extBool(basic, AUTH_MANUAL_INACTIVE_EXT) ?? false,
    status: 'active',
    notes: extString(basic, AUTH_NOTES_EXT),
  };
  draft.status = computeAuthorizationStatus(draft);
  return draft;
}

export interface BuildAuthorizationParams {
  patientRef: string;
  coverageRef?: string;
  authorizationNumber: string;
  validFrom?: string;
  validThrough?: string;
  serviceType?: string;
  limitType: AuthLimitTypeCode;
  authorizedUses?: number;
  usedCount?: number;
  reminderThreshold?: number;
  remindMonthBeforeExpiring?: boolean;
  manualInactive?: boolean;
  notes?: string;
}

export function buildInsuranceAuthorizationBasic(params: BuildAuthorizationParams): Basic {
  const usedCount = params.usedCount ?? 0;

  const extensions: Basic['extension'] = [
    ...(params.coverageRef
      ? [{ url: AUTH_COVERAGE_REF_EXT, valueReference: { reference: params.coverageRef } as Reference }]
      : []),
    { url: AUTH_NUMBER_EXT, valueString: params.authorizationNumber },
    ...(params.validFrom ? [{ url: AUTH_VALID_FROM_EXT, valueDate: params.validFrom }] : []),
    ...(params.validThrough ? [{ url: AUTH_VALID_THROUGH_EXT, valueDate: params.validThrough }] : []),
    { url: AUTH_LIMIT_TYPE_EXT, valueCode: params.limitType },
    ...(params.authorizedUses != null
      ? [{ url: AUTH_AUTHORIZED_USES_EXT, valueInteger: params.authorizedUses }]
      : []),
    { url: AUTH_USED_COUNT_EXT, valueInteger: usedCount },
    ...(params.serviceType ? [{ url: AUTH_SERVICE_TYPE_EXT, valueString: params.serviceType }] : []),
    ...(params.reminderThreshold != null
      ? [{ url: AUTH_REMINDER_THRESHOLD_EXT, valueInteger: params.reminderThreshold }]
      : []),
    ...(params.remindMonthBeforeExpiring
      ? [{ url: AUTH_REMIND_MONTH_BEFORE_EXPIRY_EXT, valueBoolean: true }]
      : []),
    ...(params.manualInactive
      ? [{ url: AUTH_MANUAL_INACTIVE_EXT, valueBoolean: true }]
      : []),
    ...(params.notes ? [{ url: AUTH_NOTES_EXT, valueString: params.notes }] : []),
  ];

  return {
    resourceType: 'Basic',
    code: {
      coding: [{
        system: INSURANCE_AUTHORIZATION_CODE_SYSTEM,
        code: INSURANCE_AUTHORIZATION_CODE,
        display: 'Insurance authorization',
      }],
    },
    subject: { reference: params.patientRef },
    created: new Date().toISOString(),
    extension: extensions,
    identifier: [{ value: params.authorizationNumber }],
  };
}

export function authStatusLabel(status: AuthStatusCode): string {
  const labels: Record<AuthStatusCode, string> = {
    active: 'Active',
    pending: 'Pending',
    expired: 'Expired',
    exhausted: 'Exhausted',
    cancelled: 'Cancelled',
  };
  return labels[status] ?? status;
}

export function authStatusColor(status: AuthStatusCode): string {
  switch (status) {
    case 'active': return 'green';
    case 'pending': return 'blue';
    case 'expired': return 'red';
    case 'exhausted': return 'red';
    case 'cancelled': return 'gray';
    default: return 'gray';
  }
}
